import { Directive, ElementRef, OnDestroy, OnInit } from "@angular/core";
import { DeviceDetector } from "@vault/web-vault/app/shared/utils/device-detector";

@Directive({
  standalone: true,
  selector: "[appElasticScroll]",
})
export class ElasticScrollDirective implements OnInit, OnDestroy {
  private readonly host: HTMLElement;
  private readonly deviceDetector = new DeviceDetector();

  private offset = 0;
  private springTimeout: ReturnType<typeof setTimeout> | null = null;
  private rafId: number | null = null;

  private readonly MAX_STRETCH = 80;
  private readonly WHEEL_DAMPING = 0.25;
  private readonly SPRING_DECAY = 0.82;
  private readonly SPRING_DELAY_MS = 60;

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {
    this.host = elementRef.nativeElement;
  }

  ngOnInit(): void {
    if (this.deviceDetector.isDesktop()) {
      this.host.addEventListener("wheel", this.onWheel, { passive: false });
    }
  }

  ngOnDestroy(): void {
    this.host.removeEventListener("wheel", this.onWheel);
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }
    if (this.springTimeout !== null) {
      clearTimeout(this.springTimeout);
    }
  }

  private onWheel = (e: WheelEvent): void => {
    if (this.host.hasAttribute("data-scroll-locked")) {
      this.resetSpring();
      return;
    }

    // Pinch-zoom and Ctrl+wheel zoom arrive as ctrlKey wheel events; leave them to the browser.
    // An inner handler that already consumed the wheel (e.g. the heatmap turning it into a
    // horizontal scroll) means the page isn't the one scrolling, so it mustn't bounce.
    if (e.ctrlKey || e.defaultPrevented) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = this.host;
    const atTop = scrollTop <= 0 && e.deltaY < 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1 && e.deltaY > 0;

    if (!atTop && !atBottom) {
      return;
    }

    // The page is at an edge, so a bounce is otherwise imminent. If the wheel is
    // over a nested scroll container that can still scroll in this direction, let
    // it handle the event natively instead — the page bounces only when it, not an
    // inner list (with overscroll-behavior: contain), is what hits top/bottom.
    // Checked after the edge test so the DOM walk is skipped on mid-page wheels.
    if (this.isConsumedByInnerScroller(e.target as Element | null, e.deltaY)) {
      return;
    }

    e.preventDefault();

    const delta = this.normalizeWheelDelta(e);
    const direction = atTop ? 1 : -1;
    const stretch = Math.abs(delta) * this.WHEEL_DAMPING * direction;

    this.offset = Math.max(-this.MAX_STRETCH, Math.min(this.MAX_STRETCH, this.offset + stretch));
    this.applyTransform();
    this.scheduleSpring();
  };

  /**
   * Walks up from the wheel target to the host, looking for a nested scrollable
   * element that can still scroll in the wheel's direction. If found, the inner
   * element should consume the wheel and the page must not bounce.
   */
  private isConsumedByInnerScroller(target: Element | null, deltaY: number): boolean {
    let el = target;
    while (el && el !== this.host) {
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      const isScrollable =
        (overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight;

      if (isScrollable) {
        const canScrollUp = el.scrollTop > 0;
        const canScrollDown = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
        if ((deltaY < 0 && canScrollUp) || (deltaY > 0 && canScrollDown)) {
          return true;
        }
      }
      el = el.parentElement;
    }
    return false;
  }

  private normalizeWheelDelta(e: WheelEvent): number {
    if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) {
      return e.deltaY * 32;
    }
    if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
      return e.deltaY * this.host.clientHeight;
    }
    return e.deltaY;
  }

  private applyTransform(): void {
    this.host.style.transform = `translateY(${this.offset}px)`;
  }

  private scheduleSpring(): void {
    if (this.springTimeout !== null) {
      clearTimeout(this.springTimeout);
    }
    this.springTimeout = setTimeout(() => this.runSpring(), this.SPRING_DELAY_MS);
  }

  private runSpring(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = requestAnimationFrame(this.springTick);
  }

  private resetSpring(): void {
    this.offset = 0;
    this.host.style.transform = "";
    if (this.springTimeout !== null) {
      clearTimeout(this.springTimeout);
      this.springTimeout = null;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private readonly springTick = (): void => {
    if (this.host.hasAttribute("data-scroll-locked")) {
      this.resetSpring();
      return;
    }
    this.offset *= this.SPRING_DECAY;
    if (Math.abs(this.offset) < 0.1) {
      this.offset = 0;
      this.rafId = null;
      this.host.style.transform = "";
      return;
    }
    this.applyTransform();
    this.rafId = requestAnimationFrame(this.springTick);
  };
}
